import { useEffect, useState } from 'react';
import {
  Alert,
  App as AntApp,
  Badge,
  Button,
  Card,
  Col,
  Descriptions,
  Input,
  Progress,
  Row,
  Skeleton,
  Space,
  Switch,
  Tabs,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

import { useAuthStore } from '@starling/core/auth/store';
import { ErrorAlert } from '@/components/ErrorAlert';
import { UsersList } from '@/users/UsersList';
import { useTemplatesForEditQuery } from '@/templates/hooks';
import { TemplatesTab } from './TemplatesTab';
import {
  useTenantSettingsQuery,
  useUpdateTenantSettingsMutation,
  type SmsBudgetStatus,
  type TenantSettingsUpdate,
} from './hooks';

const { Title, Text } = Typography;

// Settings — admin-level страница, читает данные тенанта/бота/SMS.
//
// На M2 у нас нет `/admin/tenant`-эндпоинта (см. openapi-m1.yaml — он
// планируется в Фазу 2 вместе с UI редактирования имени). Для имени тенанта
// берём `tid` из JWT через authStore — этого хватает для placeholder-карточки.
//
// M3.5+: `/admin/tenant/settings` уже есть и отдаёт `name`, `sms_kill_switch`,
// `sms_budget`. SMS-карточка работает на этом endpoint'е.
//
// Bot — placeholder-карточка. Реальные данные `@bot_username` придут из
// `/admin/tenant` в Фазе 2.
export function SettingsPage(): JSX.Element {
  const { t } = useTranslation();
  const user = useAuthStore((s) => s.user);
  const isAdmin = user?.role === 'admin';
  const [activeTab, setActiveTab] = useState('general');

  // Счётчик шаблонов для бейджа вкладки. Тот же query key, что и внутри
  // TemplatesTab → React Query дедуплицирует, лишнего запроса нет.
  const templatesQuery = useTemplatesForEditQuery();
  const templatesCount = templatesQuery.data?.items.length ?? 0;

  return (
    <div>
      <Title level={3} style={{ marginBottom: 4 }}>
        {t('settings.title')}
      </Title>
      <Text type="secondary">{t('settings.subtitle')}</Text>

      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        style={{ marginTop: 12 }}
        items={[
          {
            key: 'general',
            label: t('settings.tabs.general'),
            children: <GeneralTab isAdmin={isAdmin} />,
          },
          {
            key: 'messengers',
            label: t('settings.tabs.messengers'),
            children: <MessengersTab isAdmin={isAdmin} />,
          },
          {
            key: 'templates',
            label: (
              <span>
                {t('settings.tabs.templates')}{' '}
                <Badge
                  count={templatesCount}
                  showZero={false}
                  color="var(--accent)"
                  style={{ marginInlineStart: 4 }}
                />
              </span>
            ),
            children: <TemplatesTab isAdmin={isAdmin} />,
          },
        ]}
      />
    </div>
  );
}

// Вкладка «Общее»: данные оптики (ID/роль read-only, город/адрес/телефон
// редактируемые admin-only) + сотрудники оптики.
function GeneralTab({ isAdmin }: { isAdmin: boolean }): JSX.Element {
  const { t } = useTranslation();
  const user = useAuthStore((s) => s.user);
  const { message } = AntApp.useApp();

  const copyTenantId = (): void => {
    if (user?.tenantId) {
      void navigator.clipboard?.writeText(user.tenantId);
      void message.success(t('settings.tenant_id_copied'));
    }
  };

  return (
    <Row gutter={[16, 16]}>
      <Col xs={24} md={12}>
        <Card title={t('settings.tenant_title')}>
          <Descriptions column={1} size="small">
            <Descriptions.Item label={t('settings.tenant_name')}>
              <Space size={8}>
                <Text>{user ? t('app.title') : '—'}</Text>
                <Tag color="default">{t('settings.tenant_name_phase2')}</Tag>
              </Space>
            </Descriptions.Item>
            <Descriptions.Item label={t('settings.tenant_id')}>
              <Space size={6}>
                <span className="sl-mono">{user?.tenantId ?? '—'}</span>
                {user?.tenantId && (
                  <Button size="small" type="text" onClick={copyTenantId}>
                    {t('settings.tenant_id_copy')}
                  </Button>
                )}
              </Space>
            </Descriptions.Item>
            <Descriptions.Item label={t('settings.tenant_role')}>
              <Tag color={isAdmin ? 'blue' : 'default'}>
                {isAdmin ? t('users.role.admin') : t('users.role.employee')}
              </Tag>
            </Descriptions.Item>
          </Descriptions>
          <TenantTextField field="city" isAdmin={isAdmin} />
          <TenantTextField field="address" isAdmin={isAdmin} />
          <TenantTextField field="phone" isAdmin={isAdmin} />
          <Alert
            type="info"
            showIcon
            style={{ marginTop: 12 }}
            message={t('settings.tenant_phase2_note')}
          />
        </Card>
      </Col>

      <Col xs={24} md={12}>
        <Card title={t('settings.users_title')}>
          <UsersList />
        </Card>
      </Col>
    </Row>
  );
}

// Вкладка «Мессенджеры»: Telegram-бот (read-only) + SMS-канал (kill-switch +
// бюджет).
function MessengersTab({ isAdmin }: { isAdmin: boolean }): JSX.Element {
  const { t } = useTranslation();
  // M6-fix: реальные данные бота из API (раньше был хардкод @starling_optics_bot).
  const { data: settings } = useTenantSettingsQuery();
  const botUsername = settings?.telegram_bot_username;
  const configured = settings?.telegram_configured ?? false;
  return (
    <Row gutter={[16, 16]}>
      <Col xs={24} md={12}>
        <Card title={t('settings.bot_title')}>
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Space size="middle" align="center">
              <Badge status={configured ? 'success' : 'default'} />
              <Text strong className="sl-mono">
                {botUsername ? `@${botUsername}` : '—'}
              </Text>
              <Tag color={configured ? 'green' : 'default'}>
                {configured ? t('settings.bot_connected') : t('settings.bot_not_configured')}
              </Tag>
            </Space>
            <Text type="secondary">{t('settings.bot_description')}</Text>
            <Alert type="info" showIcon message={t('settings.bot_phase2_note')} />
          </Space>
        </Card>
      </Col>

      <Col xs={24} md={12}>
        <SmsSettingsCard isAdmin={isAdmin} />
      </Col>
    </Row>
  );
}

// Редактируемые текстовые поля оптики (M5.6 город, M5.7 адрес/телефон). Правит
// сама оптика (admin) через тот же PATCH /admin/tenant/settings. Значение может
// быть null — показываем пустой input. RBAC: employee видит значение, но
// редактировать не может (PATCH admin-only, server даст 403). Кнопка «Сохранить»
// активна только при изменении.
type TenantField = 'city' | 'address' | 'phone';

const TENANT_FIELD_META: Record<
  TenantField,
  { label: string; placeholder: string; saved: string; maxLength: number }
> = {
  city: {
    label: 'settings.city_label',
    placeholder: 'settings.city_placeholder',
    saved: 'settings.city_saved',
    maxLength: 120,
  },
  address: {
    label: 'settings.address_label',
    placeholder: 'settings.address_placeholder',
    saved: 'settings.address_saved',
    maxLength: 200,
  },
  phone: {
    label: 'settings.phone_label',
    placeholder: 'settings.phone_placeholder',
    saved: 'settings.phone_saved',
    maxLength: 40,
  },
};

function TenantTextField({
  field,
  isAdmin,
}: {
  field: TenantField;
  isAdmin: boolean;
}): JSX.Element {
  const { t } = useTranslation();
  const { message } = AntApp.useApp();
  const settingsQuery = useTenantSettingsQuery();
  const updateMutation = useUpdateTenantSettingsMutation();
  const meta = TENANT_FIELD_META[field];

  const serverValue = settingsQuery.data?.[field] ?? '';
  const [value, setValue] = useState(serverValue);

  // Синхронизируем локальный ввод, когда прилетают/обновляются данные с сервера.
  useEffect(() => {
    setValue(serverValue);
  }, [serverValue]);

  const dirty = value.trim() !== serverValue.trim();

  const handleSave = (): void => {
    const patch: TenantSettingsUpdate = { [field]: value.trim() };
    updateMutation.mutate(patch, {
      onSuccess: () => {
        void message.success(t(meta.saved));
      },
    });
  };

  if (settingsQuery.isLoading) {
    return <Skeleton.Input active size="small" style={{ marginTop: 12 }} />;
  }

  const control = (
    <Input
      value={value}
      disabled={!isAdmin}
      maxLength={meta.maxLength}
      placeholder={t(meta.placeholder)}
      onChange={(e) => setValue(e.target.value)}
      onPressEnter={() => {
        if (isAdmin && dirty) handleSave();
      }}
      data-testid={`settings-${field}-input`}
    />
  );

  return (
    <Space direction="vertical" size={4} style={{ width: '100%', marginTop: 12 }}>
      <Text strong style={{ fontSize: 13 }}>
        {t(meta.label)}
      </Text>
      <Space.Compact style={{ width: '100%' }}>
        {isAdmin ? control : <Tooltip title={t('settings.city_admin_only')}>{control}</Tooltip>}
        <Button
          type="primary"
          disabled={!isAdmin || !dirty}
          loading={updateMutation.isPending && updateMutation.variables?.[field] !== undefined}
          onClick={handleSave}
          data-testid={`settings-${field}-save`}
        >
          {t('settings.city_save')}
        </Button>
      </Space.Compact>
      {updateMutation.error && <ErrorAlert error={updateMutation.error} />}
    </Space>
  );
}

interface SmsSettingsCardProps {
  isAdmin: boolean;
}

// SMS-карточка (M3.6).
//
// Состояния:
//   * loading            — Skeleton.
//   * error              — ErrorAlert.
//   * data.kill_switch=true  → Switch выключен («SMS отключён»), бюджет всё равно показываем
//                              (исторически клиент мог потратить квоту до выключения).
//   * data.kill_switch=false → Switch включён («SMS включён»).
//
// RBAC: PATCH admin-only. Employee видит карточку (нужно понимать причины
// status='skipped'), но Switch disabled + Tooltip «требуется роль admin».
//
// Бюджет:
//   * cap=0  → «Без лимита» (текстовый плейсхолдер вместо Progress).
//   * cap>0  → Progress «used / cap» с цветом: success → warning >80% → exception 100%.
//   * quota_exhausted=true → красный Alert поверх Progress.
function SmsSettingsCard({ isAdmin }: SmsSettingsCardProps): JSX.Element {
  const { t } = useTranslation();
  const { message } = AntApp.useApp();
  const settingsQuery = useTenantSettingsQuery();
  const updateMutation = useUpdateTenantSettingsMutation();

  const handleToggle = (checked: boolean): void => {
    // Switch ON («SMS включён») = kill_switch FALSE и наоборот.
    updateMutation.mutate(
      { sms_kill_switch: !checked },
      {
        onSuccess: () => {
          void message.success(t('settings.sms.toggled'));
        },
      },
    );
  };

  if (settingsQuery.isLoading) {
    return (
      <Card title={t('settings.sms.title')}>
        <Skeleton active />
      </Card>
    );
  }

  if (settingsQuery.error) {
    return (
      <Card title={t('settings.sms.title')}>
        <ErrorAlert error={settingsQuery.error} />
      </Card>
    );
  }

  const data = settingsQuery.data;
  if (!data) {
    return (
      <Card title={t('settings.sms.title')}>
        <Text type="secondary">—</Text>
      </Card>
    );
  }

  const smsEnabled = !data.sms_kill_switch;
  const budget = data.sms_budget;

  const switchControl = (
    <Switch
      checked={smsEnabled}
      disabled={!isAdmin}
      loading={updateMutation.isPending}
      onChange={handleToggle}
    />
  );

  return (
    <Card title={t('settings.sms.title')}>
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Space size="middle" align="center" wrap>
          {isAdmin ? (
            switchControl
          ) : (
            <Tooltip title={t('settings.sms.admin_only')}>{switchControl}</Tooltip>
          )}
          <Text>
            {smsEnabled ? t('settings.sms.enabled_label') : t('settings.sms.disabled_label')}
          </Text>
        </Space>

        {updateMutation.error && <ErrorAlert error={updateMutation.error} />}

        {budget.quota_exhausted && (
          <Alert
            type="error"
            showIcon
            message={t('settings.sms.quota_exhausted_alert')}
            data-testid="sms-quota-exhausted-alert"
          />
        )}

        <Space size={6} align="center">
          <Text type="secondary" style={{ fontSize: 12 }}>
            {t('settings.sms.month_label', { month: budget.month })}
          </Text>
          <Tooltip title={t('settings.sms.quota_help')}>
            <QuestionCircleOutlined
              data-testid="sms-quota-help"
              style={{ color: 'rgba(0, 0, 0, 0.45)', fontSize: 13 }}
            />
          </Tooltip>
        </Space>

        <SmsBudgetRow
          label={t('settings.sms.daily_quota')}
          used={budget.daily_used}
          cap={budget.daily_cap}
          testIdPrefix="sms-budget-daily"
        />
        <SmsBudgetRow
          label={t('settings.sms.tenant_quota')}
          used={budget.tenant_used}
          cap={budget.tenant_cap}
          testIdPrefix="sms-budget-tenant"
        />
        <SmsBudgetRow
          label={t('settings.sms.global_cap')}
          used={budget.global_used}
          cap={budget.global_cap}
          testIdPrefix="sms-budget-global"
        />
      </Space>
    </Card>
  );
}

interface SmsBudgetRowProps {
  label: string;
  used: SmsBudgetStatus['tenant_used'];
  cap: SmsBudgetStatus['tenant_cap'];
  testIdPrefix: string;
}

// Одна строка бюджета: «Квота тенанта: 12 / 100» с Progress.
// cap=0 → «Без лимита» (нет Progress).
function SmsBudgetRow({ label, used, cap, testIdPrefix }: SmsBudgetRowProps): JSX.Element {
  const { t } = useTranslation();
  if (cap <= 0) {
    return (
      <Space direction="vertical" size={2} style={{ width: '100%' }} data-testid={testIdPrefix}>
        <Text strong style={{ fontSize: 13 }}>
          {label}
        </Text>
        <Text type="secondary">{t('settings.sms.unlimited')}</Text>
      </Space>
    );
  }
  const percent = Math.min(100, Math.round((used / cap) * 100));
  // AntD Progress status: success (зелёный) → normal/warning (жёлтый) >80% → exception 100%.
  // Через `status` контролируем цвет напрямую — это надёжнее, чем strokeColor по числу.
  let status: 'success' | 'normal' | 'exception' = 'success';
  if (percent >= 100) status = 'exception';
  else if (percent >= 80) status = 'normal';
  return (
    <Space direction="vertical" size={2} style={{ width: '100%' }} data-testid={testIdPrefix}>
      <Space style={{ width: '100%', justifyContent: 'space-between' }}>
        <Text strong style={{ fontSize: 13 }}>
          {label}
        </Text>
        <Text type="secondary" className="sl-mono" style={{ fontSize: 12 }}>
          {used} / {cap}
        </Text>
      </Space>
      <Progress
        percent={percent}
        status={status}
        showInfo={false}
        size="small"
        strokeColor={percent >= 80 && percent < 100 ? '#faad14' : undefined}
      />
    </Space>
  );
}
